// A searchable, controlled combobox. Options come from `loadOptions(query)` (a server search), debounced; a reply that
// arrives after a newer query was sent is ignored. Keyboard: ↑/↓ move, Enter picks (or, with allowCustom and nothing
// highlighted, keeps the typed text), Escape closes. With allowCustom nothing is highlighted until an arrow key is
// pressed, so Enter on typed text keeps the text. A click outside closes. The list is position:fixed so a scrolling
// sheet does not clip it.

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';

export interface SearchSelectProps<T> {
  /** The selected option, or null. */
  value: T | null;
  onChange: (value: T | null) => void;
  /** Server search. Called with '' when the list opens empty. */
  loadOptions: (query: string) => Promise<T[]>;
  /** Stable key per option. */
  getOptionKey: (option: T) => string;
  /** Text shown in the input for the selected option. */
  getOptionLabel: (option: T) => string;
  /** One row of the list; defaults to the label. */
  renderOption?: (option: T, active: boolean) => ReactNode;
  placeholder?: string;
  disabled?: boolean;
  /** Free text + Enter (with no option highlighted) clears the selection and calls onCustom with the text. */
  allowCustom?: boolean;
  onCustom?: (text: string) => void;
  /** Debounce before a search is sent, ms. */
  debounceMs?: number;
  /** Changing this re-runs the search (e.g. a filter the options depend on changed). */
  reloadKey?: string;
  className?: string;
  'aria-label'?: string;
}

const CONTROL = 'h-8 w-full rounded-lg border border-ink-200 bg-white px-2.5 pr-8 text-[12.5px] text-ink-900 placeholder:text-ink-400 outline-none transition focus:border-bronze-500 focus:ring-2 focus:ring-bronze-500/30 disabled:bg-ink-50 disabled:text-ink-400';

export function SearchSelect<T>({
  value, onChange, loadOptions, getOptionKey, getOptionLabel, renderOption, placeholder, disabled, allowCustom = false, onCustom,
  debounceMs = 200, reloadKey, className = '', 'aria-label': ariaLabel,
}: SearchSelectProps<T>) {
  const listId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const seq = useRef(0);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value ? getOptionLabel(value) : '');
  const [result, setResult] = useState<{ key: string; options: T[]; error: string | null }>({ key: '', options: [], error: null });
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);

  // The selection changed from outside (or was picked): show its label. Adjusted during render, not in an effect.
  const valueKey = value ? getOptionKey(value) : null;
  const [shownKey, setShownKey] = useState(valueKey);
  if (shownKey !== valueKey) {
    setShownKey(valueKey);
    if (value) setText(getOptionLabel(value));
  }

  // What is being searched for. With the selection's own label in the box, the list shows everything.
  const query = value && text === getOptionLabel(value) ? '' : text.trim();
  const searchKey = `${query}\u0000${reloadKey ?? ''}`;
  const loading = open && result.key !== searchKey;
  const options = result.key === searchKey ? result.options : [];
  const error = result.key === searchKey ? result.error : null;

  // Debounced search while open; a reply for anything but the latest request is dropped.
  useEffect(() => {
    if (!open) return;
    const mine = ++seq.current;
    const t = setTimeout(() => {
      loadOptions(query).then(
        (rows) => { if (mine !== seq.current) return; setResult({ key: searchKey, options: rows, error: null }); setActive(rows.length && !allowCustom ? 0 : -1); },
        (e: unknown) => { if (mine !== seq.current) return; setResult({ key: searchKey, options: [], error: e instanceof Error ? e.message : 'Search failed' }); setActive(-1); },
      );
    }, debounceMs);
    return () => clearTimeout(t);
  }, [open, searchKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = useCallback(() => {
    seq.current++;   // any reply still in flight is now stale
    setOpen(false);
    setActive(-1);
    // Back to the selection's label; free text stays only when it is allowed.
    if (value) setText(getOptionLabel(value));
    else if (!allowCustom) setText('');
  }, [value, allowCustom, getOptionLabel]);

  // Click outside closes.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!wrapRef.current?.contains(t) && !listRef.current?.contains(t)) close();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  // Place the list under the input (or above it when there is no room below), following scroll and resize.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = inputRef.current?.getBoundingClientRect();
      if (!r) return;
      const width = Math.min(Math.max(r.width, 320), window.innerWidth - 16);
      const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
      const below = window.innerHeight - r.bottom - 8;
      const above = r.top - 8;
      const up = below < 220 && above > below;
      const maxHeight = Math.min(300, up ? above : below);
      setPos({ top: up ? r.top - 4 - maxHeight : r.bottom + 4, left, width, maxHeight });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open]);

  // Keep the highlighted option in view.
  useEffect(() => {
    if (active < 0) return;
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const pick = (o: T) => {
    onChange(o);
    setText(getOptionLabel(o));
    seq.current++;
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      if (!options.length) return;
      setActive((i) => (e.key === 'ArrowDown' ? (i + 1) % options.length : (i <= 0 ? options.length - 1 : i - 1)));
    } else if (e.key === 'Enter') {
      if (!open) return;
      e.preventDefault();
      const o = active >= 0 ? options[active] : undefined;
      if (o && !loading) pick(o);
      else if (allowCustom && text.trim()) {
        if (value) onChange(null);
        onCustom?.(text.trim());
        seq.current++;
        setOpen(false);
        setActive(-1);
      }
    } else if (e.key === 'Escape') {
      if (!open) return;
      // Close the list only, not the sheet the combobox sits in.
      e.preventDefault();
      e.nativeEvent.stopImmediatePropagation();
      e.stopPropagation();
      close();
    } else if (e.key === 'Tab' && open) {
      close();
    }
  };

  const activeId = open && active >= 0 && options[active] ? `${listId}-opt-${active}` : undefined;

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={activeId}
        autoComplete="off"
        disabled={disabled}
        placeholder={placeholder}
        value={text}
        onChange={(e) => { setText(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className={CONTROL}
      />
      {(value || text) && !disabled && (
        <button type="button" tabIndex={-1} aria-label="Clear"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => { if (value) onChange(null); setText(''); inputRef.current?.focus(); }}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md p-1 text-ink-400 transition hover:bg-ink-100 hover:text-ink-700">
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      {open && pos && (
        <ul ref={listRef} id={listId} role="listbox" aria-label={ariaLabel}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
          className="z-[200] overflow-y-auto rounded-lg border border-ink-200 bg-white py-1 shadow-float">
          {options.map((o, i) => (
            <li key={getOptionKey(o)} id={`${listId}-opt-${i}`} data-index={i} role="option" aria-selected={i === active}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(o)}
              className={`cursor-pointer px-3 py-1.5 text-[12.5px] ${i === active ? 'bg-ink-100 text-ink-900' : 'text-ink-800'}`}>
              {renderOption ? renderOption(o, i === active) : getOptionLabel(o)}
            </li>
          ))}
          {loading && !options.length && <li role="presentation" className="px-3 py-1.5 text-[12.5px] text-ink-400">Searching…</li>}
          {!loading && error && <li role="presentation" className="px-3 py-1.5 text-[12.5px] text-red-600">{error}</li>}
          {!loading && !error && !options.length && <li role="presentation" className="px-3 py-1.5 text-[12.5px] text-ink-400">No matches</li>}
          {allowCustom && text.trim() && !(value && text === getOptionLabel(value)) && (
            <li role="presentation" className="border-t border-ink-100 px-3 py-2 text-[11.5px] text-ink-400">
              {active >= 0 ? 'Enter picks the highlighted item.' : <>Press Enter to use “{text.trim()}” as a description.</>}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
